/**
 * JSON Export - Utility Functions
 *
 * Creates export data from localStorage state and triggers JSON file download.
 * Follows the same Blob → URL → <a> click → cleanup pattern as downloadCSV.
 */

import type {
  SimulatorInput,
  SavedSimulation,
  SimulatorExportData,
} from '../types';
import { EXPORT_VERSION } from '../types';
import type { FuelPrice, InvestmentCostDefinition } from '../heating-cost';
import type { UserPreferences } from '../hooks/useLocalStorage';
import type { HeatingCostSettings } from '../heating-cost/hooks/useHeatingCostConfig';

/**
 * Creates a complete export data object from all simulator state.
 */
export function createExportData(
  formState: SimulatorInput,
  savedSimulations: SavedSimulation[],
  preferences: UserPreferences,
  fuelPrices: FuelPrice[],
  investmentCosts: InvestmentCostDefinition[],
  settings: HeatingCostSettings
): SimulatorExportData {
  return {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    formState,
    savedSimulations,
    preferences,
    heatingCost: {
      fuelPrices,
      investmentCosts,
      settings,
    },
  };
}

/**
 * Generates a default filename with current date and time.
 */
function generateFilename(): string {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, '');
  const time = now.toTimeString().slice(0, 8).replace(/:/g, '');
  return `symulacja_certyfikat_energetyczny_${date}_${time}.json`;
}

/**
 * Downloads a SimulatorExportData object as a JSON file.
 * Uses the same pattern as the existing downloadCSV utility.
 *
 * @param data - The export data to download
 * @param filename - Optional custom filename (defaults to auto-generated)
 */
export function downloadJSON(data: SimulatorExportData, filename?: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? generateFilename();
  anchor.style.display = 'none';

  document.body.appendChild(anchor);
  anchor.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}
